import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

// Progress bar component
function ProgressBar({ value, max, color = 'blue' }) {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const colors = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    amber: 'bg-amber-500'
  };
  
  return (
    <div className="w-full bg-slate-200 rounded-full h-2">
      <div 
        className={`h-2 rounded-full ${colors[color]} transition-all duration-500`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

// Enrollment row component
function EnrollmentRow({ enrollment, onAction }) {
  const [loading, setLoading] = useState(false);

  const handleAction = async (action) => {
    setLoading(true);
    await onAction(enrollment.id, action);
    setLoading(false);
  };

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    paused: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700',
    replied: 'bg-purple-100 text-purple-700',
    removed: 'bg-slate-100 text-slate-500 dark:text-slate-400'
  };

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900">
      <td className="px-4 py-3">
        <Link to={`/leads/${enrollment.lead_id}`} className="font-medium text-slate-900 dark:text-white hover:text-blue-600">
          {enrollment.lead_name}
        </Link>
        <p className="text-xs text-slate-500 dark:text-slate-400">{enrollment.lead_email}</p>
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[enrollment.status]}`}>
          {enrollment.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        Step {enrollment.current_step + 1}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {enrollment.emails_sent}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {enrollment.emails_opened}
      </td>
      <td className="px-4 py-3 text-xs text-slate-400">
        {enrollment.enrolled_at ? format(parseISO(enrollment.enrolled_at), 'MMM d, HH:mm') : 'N/A'}
      </td>
      <td className="px-4 py-3">
        {enrollment.status === 'active' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleAction('pause')}
              disabled={loading}
              className="text-xs text-amber-600 hover:text-amber-700"
            >
              Pause
            </button>
            <button
              onClick={() => handleAction('skip_step')}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Skip Step
            </button>
            <button
              onClick={() => handleAction('remove')}
              disabled={loading}
              className="text-xs text-red-600 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        )}
        {enrollment.status === 'paused' && (
          <button
            onClick={() => handleAction('resume')}
            disabled={loading}
            className="text-xs text-green-600 hover:text-green-700"
          >
            Resume
          </button>
        )}
      </td>
    </tr>
  );
}

// Enroll Leads Modal
function EnrollModal({ isOpen, onClose, sequenceId, onEnrolled }) {
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      axios.get(`${API}/leads`).then(res => {
        setLeads(res.data);
        setLoading(false);
      });
    }
  }, [isOpen]);

  const filteredLeads = leads.filter(lead => 
    lead.name?.toLowerCase().includes(search.toLowerCase()) ||
    lead.email?.toLowerCase().includes(search.toLowerCase()) ||
    lead.organization?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleEnroll = async () => {
    if (selectedLeads.length === 0) return;
    
    setEnrolling(true);
    try {
      const response = await axios.post(`${API}/sequences/${sequenceId}/enroll`, {
        lead_ids: selectedLeads
      });
      onEnrolled(response.data);
      onClose();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to enroll leads');
    } finally {
      setEnrolling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[600px] md:max-h-[80vh] bg-white dark:bg-slate-800 rounded-xl shadow-2xl z-50 flex flex-col">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 dark:text-white">Enroll Leads in Sequence</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <input
            type="text"
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
          ) : filteredLeads.length === 0 ? (
            <p className="text-center text-slate-500 dark:text-slate-400 py-8">No leads found</p>
          ) : (
            <div className="space-y-2">
              {filteredLeads.map(lead => (
                <label
                  key={lead.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedLeads.includes(lead.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.id)}
                    onChange={() => toggleLead(lead.id)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{lead.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{lead.email} • {lead.organization || 'No company'}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                    lead.status === 'qualified' ? 'bg-green-100 text-green-700' :
                    'bg-slate-100 text-slate-600 dark:text-slate-400'
                  }`}>
                    {lead.status}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
              Cancel
            </button>
            <button
              onClick={handleEnroll}
              disabled={selectedLeads.length === 0 || enrolling}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50"
            >
              {enrolling ? 'Enrolling...' : `Enroll ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Main Sequence Detail Page
export default function SequenceDetail() {
  const { sequenceId } = useParams();
  const navigate = useNavigate();
  
  const [sequence, setSequence] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, enrollments, steps

  const fetchData = useCallback(async () => {
    try {
      const [seqRes, analyticsRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/sequences/${sequenceId}`),
        axios.get(`${API}/sequences/${sequenceId}/analytics`),
        axios.get(`${API}/sequences/${sequenceId}/enrollments`)
      ]);
      
      setSequence(seqRes.data);
      setAnalytics(analyticsRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      console.error('Error fetching sequence:', error);
      navigate('/sequences');
    } finally {
      setLoading(false);
    }
  }, [sequenceId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEnrollmentAction = async (enrollmentId, action) => {
    await axios.post(`${API}/sequences/${sequenceId}/enrollments/${enrollmentId}/action`, { action });
    fetchData();
  };

  const handleToggleActive = async () => {
    if (sequence.is_active) {
      await axios.post(`${API}/sequences/${sequenceId}/deactivate`);
    } else {
      await axios.post(`${API}/sequences/${sequenceId}/activate`);
    }
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!sequence) return null;

  const overall = analytics?.overall || {};

  return (
    <div className="space-y-6" data-testid="sequence-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link to="/sequences" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{sequence.name}</h1>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              sequence.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600 dark:text-slate-400'
            }`}>
              {sequence.is_active ? 'Active' : 'Draft'}
            </span>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{sequence.description || 'No description'}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEnrollModal(true)}
            disabled={!sequence.is_active}
            className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Enroll Leads
          </button>
          <button
            onClick={handleToggleActive}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${
              sequence.is_active 
                ? 'text-amber-700 bg-amber-100 hover:bg-amber-200' 
                : 'text-green-700 bg-green-100 hover:bg-green-200'
            }`}
          >
            {sequence.is_active ? 'Pause' : 'Activate'}
          </button>
          <Link
            to={`/sequences/${sequenceId}/edit`}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
          >
            Edit
          </Link>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{overall.total_enrolled || 0}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Enrolled</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{overall.active || 0}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Active</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{overall.completed || 0}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Completed</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{overall.emails_sent || 0}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Emails Sent</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{overall.open_rate || 0}%</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Open Rate</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{overall.click_rate || 0}%</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Click Rate</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{overall.reply_rate || 0}%</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Reply Rate</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700">
        {['overview', 'enrollments', 'steps'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white'
            }`}
          >
            {tab === 'overview' ? 'Overview' : tab === 'enrollments' ? `Enrollments (${enrollments.length})` : 'Steps'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Per-Step Performance */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Step Performance</h3>
            <div className="space-y-4">
              {analytics?.per_step?.map((step, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Step {idx + 1}: {step.subject?.substring(0, 30)}...</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{step.open_rate}% opened</span>
                  </div>
                  <ProgressBar value={step.open_rate} max={100} color="green" />
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
                    <span>{step.sent} sent</span>
                    <span>{step.opened} opened</span>
                    <span>{step.clicked} clicked</span>
                    <span>{step.replied} replied</span>
                  </div>
                </div>
              )) || (
                <p className="text-sm text-slate-500 dark:text-slate-400">No data yet</p>
              )}
            </div>
          </div>

          {/* Sequence Settings */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Settings</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Trigger</span>
                <span className="text-slate-900 dark:text-white capitalize">{sequence.trigger?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Send Window</span>
                <span className="text-slate-900 dark:text-white">{sequence.send_window_start}:00 - {sequence.send_window_end}:00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Daily Limit</span>
                <span className="text-slate-900 dark:text-white">{sequence.daily_limit} emails</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Stop on Reply</span>
                <span className="text-slate-900 dark:text-white">{sequence.stop_on_reply ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Total Steps</span>
                <span className="text-slate-900 dark:text-white">{sequence.steps?.length || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'enrollments' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Lead</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Current Step</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Sent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Opened</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Enrolled</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {enrollments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                      No leads enrolled yet
                    </td>
                  </tr>
                ) : (
                  enrollments.map(enrollment => (
                    <EnrollmentRow
                      key={enrollment.id}
                      enrollment={enrollment}
                      onAction={handleEnrollmentAction}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'steps' && (
        <div className="space-y-4">
          {sequence.steps?.map((step, idx) => (
            <div key={step.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                  {idx + 1}
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">{step.subject}</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {idx === 0 ? 'Sent immediately' : `Wait ${step.delay_days} days ${step.delay_hours > 0 ? `${step.delay_hours}h` : ''}`}
                  </p>
                </div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                {step.body.substring(0, 300)}...
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Enroll Modal */}
      <EnrollModal
        isOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        sequenceId={sequenceId}
        onEnrolled={(result) => {
          fetchData();
          alert(`Enrolled ${result.enrolled} leads, skipped ${result.skipped}`);
        }}
      />
    </div>
  );
}
